import { addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import {
  PAYMENT_RUN_HOUR,
  PAYMENT_RUN_TIMEZONE,
} from './constants';

/**
 * ENR-4013: dueDate is date-only (UTC midnight of the studio calendar day).
 * Read the UTC calendar day so Chicago/Auckland viewers do not shift a day.
 */
export function formatDueDateLabel(dueDate: string): string {
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
      const [y, m, d] = dueDate.split('-').map(Number);
      return new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).toLocaleDateString(
        'en-US',
        { timeZone: 'UTC' },
      );
    }
    const parsed = new Date(dueDate);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleDateString('en-US', { timeZone: 'UTC' });
  } catch (error) {
    console.error('Failed to format payment run due date', { dueDate, error });
    return '—';
  }
}

export function formatMoney(amount: number, symbol = '$'): string {
  return `${symbol}${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatCountdown(nextRunAt: string): string {
  const target = new Date(nextRunAt).getTime();
  const now = Date.now();
  const diff = target - now;
  if (diff <= 0) return '—';
  const totalSeconds = Math.floor(diff / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

export function formatNextRunAt(nextRunAt: string): string {
  try {
    const d = new Date(nextRunAt);
    if (Number.isNaN(d.getTime())) return nextRunAt;
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return nextRunAt;
  }
}

export function formatInstantInEastern(ms: number): string {
  return formatInTimeZone(
    new Date(ms),
    PAYMENT_RUN_TIMEZONE,
    "EEEE, MMMM d, yyyy 'at' h:mm a zzz",
  );
}

export function formatScheduledRunEastern(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return formatInTimeZone(
      d,
      PAYMENT_RUN_TIMEZONE,
      "EEEE, MMMM d, yyyy 'at' h:mm a zzz",
    );
  } catch {
    return iso;
  }
}

/** Compact date · time TZ for the filter row (same zone as payment run). */
export function formatScheduledRunHeaderLine(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return formatScheduledRunEastern(iso);
    const datePart = formatInTimeZone(
      d,
      PAYMENT_RUN_TIMEZONE,
      'MMM d, yyyy',
    );
    const timePart = formatInTimeZone(d, PAYMENT_RUN_TIMEZONE, 'h:mm a');
    const tzPart = formatInTimeZone(d, PAYMENT_RUN_TIMEZONE, 'zzz');
    return `${datePart} · ${timePart} ${tzPart}`;
  } catch {
    return formatScheduledRunEastern(iso);
  }
}

/** Next scheduled run is 10:00 AM America/New_York. Returns YYYY-MM-DD for the date of that run. */
export function getNextRunDateString(): string {
  const now = new Date();
  const etDateStr = formatInTimeZone(now, PAYMENT_RUN_TIMEZONE, 'yyyy-MM-dd');
  const etHour = parseInt(formatInTimeZone(now, PAYMENT_RUN_TIMEZONE, 'H'), 10);
  if (etHour < PAYMENT_RUN_HOUR) return etDateStr;
  const parts = etDateStr.split('-').map(Number);
  const y = parts[0] ?? 0;
  const monthIndex = (parts[1] ?? 1) - 1;
  const day = parts[2] ?? 1;
  const noonUtcToday = new Date(Date.UTC(y, monthIndex, day, 12, 0, 0));
  const tomorrowUtc = addDays(noonUtcToday, 1);
  return formatInTimeZone(tomorrowUtc, PAYMENT_RUN_TIMEZONE, 'yyyy-MM-dd');
}

/** First day of the month (YYYY-MM-DD) for the given date string (YYYY-MM-DD). */
export function firstOfMonth(dateStr: string): string {
  const [y, m] = dateStr.split('-').map(Number);
  return `${y}-${String(m).padStart(2, '0')}-01`;
}
